import { makeStyles, tokens } from "@fluentui/react-components";
import { flexChildSizeFixed, flexParent, flexParentCenterCrossAxis, textClip } from "@ms/planner-generic-controls/lib/local/utilities/Mixin.styles";
/**
 * Menu positioning configuration
 */ export const menuPositioning = {
    position: "below",
    align: "end",
    offset: {
        crossAxis: -130,
        mainAxis: 4
    }
};
/**
 * Styles for LeftNavNewPlanSection
 */ export const useLeftNavNewPlanSectionStyles = makeStyles({
    newPlanSection: {
        ...flexParent("column"),
        width: "100%"
    },
    splitButtonContainer: {
        ...flexParent("row"),
        ...flexParentCenterCrossAxis,
        maxWidth: "154px",
        height: tokens.spacingVerticalXXXL,
        marginTop: "auto",
        marginBottom: tokens.spacingVerticalM,
        marginLeft: tokens.spacingHorizontalL,
        marginRight: tokens.spacingHorizontalXXL,
        gap: "0",
        position: "relative",
        overflow: "visible"
    },
    // Split button dropdown section
    splitButtonDropdown: {
        ...flexChildSizeFixed(tokens.spacingHorizontalXXL),
        border: "none",
        borderLeft: `1px solid ${tokens.colorNeutralStrokeOnBrand2}`,
        borderRadius: `0 ${tokens.borderRadiusXLarge} ${tokens.borderRadiusXLarge} 0`,
        padding: "0",
        minWidth: tokens.spacingHorizontalXXL,
        maxWidth: tokens.spacingHorizontalXXL,
        "&:hover": {
            borderLeft: `1px solid ${tokens.colorNeutralStrokeOnBrand2}`
        },
        "&:focus": {
            outline: "none",
            border: "none",
            borderLeft: `1px solid ${tokens.colorNeutralStrokeOnBrand2}`,
            boxShadow: "none"
        },
        "&:focus-visible": {
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: tokens.spacingVerticalXXS,
            border: "none",
            borderLeft: `1px solid ${tokens.colorNeutralStrokeOnBrand2}`,
            boxShadow: "none"
        },
        "&:active": {
            borderLeft: `1px solid ${tokens.colorNeutralStrokeOnBrand2}`
        }
    },
    // Base styles shared by both split and newplan button
    newPlanButtonBase: {
        height: tokens.spacingVerticalXXXL,
        border: "none",
        borderRadius: tokens.borderRadiusXLarge,
        fontFamily: tokens.fontFamilyBase,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        // New plan button content alignment (required for proper layout)
        "& .fui-Button__icon": {
            margin: "0",
            padding: "0"
        },
        "&:focus": {
            outline: "none",
            border: "none",
            boxShadow: "none"
        },
        "&:focus-visible": {
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: tokens.spacingVerticalXXS,
            border: "none",
            boxShadow: "none"
        }
    },
    // Split button specific styles (extends base)
    newPlanButtonWithSplit: {
        minWidth: 0,
        flexGrow: 1,
        borderRadius: `${tokens.borderRadiusXLarge} 0 0 ${tokens.borderRadiusXLarge}`,
        padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalS} ${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalSNudge}`,
        gap: tokens.spacingHorizontalSNudge,
        justifyContent: "flex-start"
    },
    buttonText: {
        ...textClip("ellipsis"),
        fontFamily: tokens.fontFamilyBase,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase300,
        minWidth: 0,
        height: tokens.spacingVerticalXL,
        margin: "0",
        padding: "0",
        paddingBottom: tokens.spacingVerticalXXS
    },
    menuPopover: {
        minWidth: "200px",
        borderRadius: tokens.borderRadiusXLarge
    },
    menuItem: {
        border: "none",
        outline: "none",
        "&:focus": {
            outline: "none",
            border: "none"
        },
        "&:focus-visible": {
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: `-${tokens.spacingVerticalXXS}`,
            border: "none"
        },
        "&:hover": {
            border: "none",
            outline: "none"
        }
    },
    buttonIcon: {
        fontSize: tokens.spacingVerticalXL,
        width: tokens.spacingVerticalXL,
        height: tokens.spacingVerticalXL
    },
    // Icon specifically for the dropdown chevron button
    dropdownChevronIcon: {
        fontSize: tokens.spacingVerticalM,
        width: tokens.spacingVerticalM,
        height: tokens.spacingVerticalM,
        margin: "0",
        padding: "0"
    },
    calloutContent: {
        padding: tokens.spacingVerticalL,
        maxWidth: "300px",
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalM
    },
    calloutHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: tokens.spacingHorizontalS
    },
    calloutTitle: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
        flex: 1
    },
    calloutDismissButton: {
        minWidth: tokens.spacingVerticalXXXL,
        height: tokens.spacingVerticalXXXL,
        padding: "0",
        "&:focus-visible": {
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: tokens.spacingVerticalXXS
        }
    },
    calloutDescription: {
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300
    },
    calloutLink: {
        fontSize: tokens.fontSizeBase300,
        lineHeight: tokens.lineHeightBase300,
        textDecoration: "none",
        border: "none",
        outline: "none",
        "&:hover": {
            textDecoration: "underline"
        },
        "&:focus": {
            textDecoration: "underline",
            outline: "none",
            border: "none"
        },
        "&:focus-visible": {
            textDecoration: "underline",
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: tokens.spacingVerticalXXS,
            border: "none"
        }
    },
    // New Plan button specific styles
    newPlanButton: {
        minWidth: "141px",
        borderRadius: tokens.borderRadiusXLarge,
        marginLeft: tokens.spacingHorizontalL,
        marginRight: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalM,
        paddingTop: tokens.spacingVerticalSNudge,
        paddingRight: tokens.spacingHorizontalM,
        paddingBottom: tokens.spacingVerticalSNudge,
        paddingLeft: tokens.spacingHorizontalSNudge,
        gap: tokens.spacingHorizontalSNudge,
        justifyContent: "flex-start",
        "&:focus-visible": {
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: tokens.spacingVerticalXXS
        }
    }
});
